import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../utils/api';

interface ChristmasEntry {
  clientName: string;
  childName: string;
  childAge: string;
  childGender: 'boy' | 'girl' | 'prefer-not-to-say' | '';
  toyPreference?: string;
}

interface SubmissionInfo {
  uniqueId: string;
  counsellorName: string;
  entries: ChristmasEntry[];
  hasSubmitted: boolean;
}

const ChristmasForm: React.FC = () => {
  const { uniqueId } = useParams<{ uniqueId: string }>();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submissionInfo, setSubmissionInfo] = useState<SubmissionInfo | null>(null);
  
  const [entries, setEntries] = useState<ChristmasEntry[]>([
    { clientName: '', childName: '', childAge: '', childGender: '', toyPreference: '' }
  ]);

  const fetchSubmissionInfo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/christmas/${uniqueId}`);
      setSubmissionInfo(response.data);
      setError('');
    } catch (err: any) {
      // Check if link has expired (410 Gone status)
      if (err.response?.status === 410 || err.response?.data?.expired) {
        // Redirect to evolving-minds.org.uk
        window.location.href = 'https://evolving-minds.org.uk';
        return;
      }
      setError(err.response?.data?.error || 'Invalid link. Please check your URL.');
    } finally {
      setLoading(false);
    }
  }, [uniqueId]);

  useEffect(() => {
    fetchSubmissionInfo();
  }, [fetchSubmissionInfo]);

  const handleAddEntry = () => {
    setEntries([...entries, { clientName: '', childName: '', childAge: '', childGender: '', toyPreference: '' }]);
  };

  const handleRemoveEntry = (index: number) => {
    if (entries.length > 1) {
      setEntries(entries.filter((_, i) => i !== index));
    }
  };

  const handleEntryChange = (index: number, field: keyof ChristmasEntry, value: string) => {
    const newEntries = [...entries];
    newEntries[index][field] = value as any;
    setEntries(newEntries);
  };

  const validateEntries = (): boolean => {
    for (const entry of entries) {
      if (!entry.clientName.trim()) {
        setError('Please enter the client name for all entries');
        return false;
      }
      if (!entry.childName.trim()) {
        setError('Please enter the child name for all entries');
        return false;
      }
      if (!entry.childAge.trim()) {
        setError('Please enter the child age for all entries');
        return false;
      }
      const age = parseInt(entry.childAge);
      if (isNaN(age) || age < 0 || age > 18) {
        setError('Child age must be between 0 and 18');
        return false;
      }
      if (!entry.childGender) {
        setError('Please select the child gender for all entries');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateEntries()) {
      return;
    }

    try {
      setSubmitting(true);
      
      // Convert age to number
      const formattedEntries = entries.map(entry => ({
        ...entry,
        childAge: parseInt(entry.childAge)
      }));

      await api.post(`/api/christmas/${uniqueId}/submit`, {
        entries: formattedEntries
      });

      setSuccess(`Successfully submitted ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}! Thank you.`);
      
      // Reset form
      setEntries([{ clientName: '', childName: '', childAge: '', childGender: '', toyPreference: '' }]);
      
      // Refresh submission info
      await fetchSubmissionInfo();
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      // Check if link has expired
      if (err.response?.status === 410 || err.response?.data?.expired) {
        window.location.href = 'https://evolving-minds.org.uk';
        return;
      }
      setError(err.response?.data?.error || 'Failed to submit entries. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !submissionInfo) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1 }}>
          🎄 Christmas Gift Collection 🎁
        </Typography>
        
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
          Submitting as: <strong>{submissionInfo?.counsellorName}</strong>
        </Typography>

        {submissionInfo && submissionInfo.hasSubmitted && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You have already submitted {submissionInfo.entries.length} {submissionInfo.entries.length === 1 ? 'entry' : 'entries'}. 
            You can add more below if needed.
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {entries.map((entry, index) => (
            <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, position: 'relative' }}>
              <Typography variant="h6" gutterBottom>
                Entry {index + 1}
              </Typography>
              
              {entries.length > 1 && (
                <IconButton
                  onClick={() => handleRemoveEntry(index)}
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}

              <TextField
                fullWidth
                label="Client Name"
                value={entry.clientName}
                onChange={(e) => handleEntryChange(index, 'clientName', e.target.value)}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Child's Name"
                value={entry.childName}
                onChange={(e) => handleEntryChange(index, 'childName', e.target.value)}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Child's Age"
                type="number"
                value={entry.childAge}
                onChange={(e) => handleEntryChange(index, 'childAge', e.target.value)}
                required
                inputProps={{ min: 0, max: 18 }}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth required sx={{ mb: 2 }}>
                <InputLabel>Child's Gender</InputLabel>
                <Select
                  value={entry.childGender}
                  label="Child's Gender"
                  onChange={(e) => handleEntryChange(index, 'childGender', e.target.value)}
                >
                  <MenuItem value="boy">Boy</MenuItem>
                  <MenuItem value="girl">Girl</MenuItem>
                  <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Toy Preference (Optional)"
                value={entry.toyPreference || ''}
                onChange={(e) => handleEntryChange(index, 'toyPreference', e.target.value)}
                multiline
                rows={2}
                placeholder="e.g., Dolls, Cars, Books, Sports equipment..."
              />
            </Box>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddEntry}
            variant="outlined"
            fullWidth
            sx={{ mb: 3 }}
          >
            Add Another Child
          </Button>

          <Divider sx={{ my: 3 }} />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : `Submit ${entries.length} ${entries.length === 1 ? 'Entry' : 'Entries'}`}
          </Button>
        </form>
      </Paper>
    </Container>
  );
};

export default ChristmasForm;
